import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { closeModal } from "../../redux/actions/actions";
import { useDispatch } from "react-redux";
import axiosInstance from "../../axiosInstance";
import Swal from "sweetalert2";
import PhoneCountryInput from "../PhoneCountryInput";
import { useTranslation } from "react-i18next";

// Shared Create Student modal. Used standalone (Students page) and from the quote flow.
// All quote-flow props are optional; when omitted it behaves as plain create.
export default function CreateStudentModal({
  setSelectedStudent,
  setStudents,
  setNewStudentControl,
  handleUpdateQuote,
  getStudents,
  onCreated,
}) {
  const initialState = {
    first_name: "",
    last_name: "",
    email_address: "",
    phone: "",
    student_gender: "",
    form_message: "",
  };

  const { t } = useTranslation();

  const [formValues, setFormValues] = useState(initialState);
  const [formErrors, setFormErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const handleClose = () => dispatch(closeModal());

  const handleChange = (event) => {
    const { name, value } = event.target;
    const lettersRegex = /^[A-Za-z]+$/;
    if (name === "branch") {
      if (value.match(lettersRegex) || value === "") {
        setFormValues((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!/\S+@\S+\.\S+/.test(formValues?.email_address)) {
      errors.email_address = t("emailIsNotValid");
    }
    if (!formValues?.phoneIsValid) {
      errors.phone = t("phoneIsNotValid");
    }
    return errors;
  };

  useEffect(() => {
    if (formValues?.email_address) {
      setFormErrors(validateForm());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  const handleCreateStudent = async () => {
    const url = `${process.env.REACT_APP_BASE_URL}auth/students`;
    const accessToken = localStorage.getItem("accessToken");
    const token = localStorage.getItem("loginUser");

    const formattedMessage = (formValues.form_message || "").replace(/\n/g, "<br><br>");
    const htmlMessage = `<p style="color: #9b9ea2">${formattedMessage}</p>`;

    const appendForm = {
      student_firstname: formValues?.first_name,
      student_lastname: formValues?.last_name,
      student_email: formValues?.email_address,
      student_phone: formValues?.phone,
      student_gender: formValues?.student_gender,
      form_message: htmlMessage,
    };

    setLoader(true);
    try {
      const response = await axiosInstance.post(url, appendForm, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          XRefreshToken: `${token}`,
          "Content-Type": "application/json",
        },
      });
      setLoader(false);
      const student = response?.data?.student;

      if (setNewStudentControl) setNewStudentControl(true);
      if (setSelectedStudent && student) {
        setSelectedStudent({
          student_id: student.student_id,
          student_name: `${student.student_firstname} ${student.student_lastname}`,
        });
      }
      if (getStudents) getStudents();
      if (handleUpdateQuote && student) handleUpdateQuote(student);
      if (onCreated && student) onCreated(student);

      Swal.fire({
        icon: "success",
        title: `${t("userCreatedSuccessfully")}!`,
        showConfirmButton: false,
        showCloseButton: true,
        timer: 1500,
      });
      handleClose();
    } catch (error) {
      setLoader(false);
      handleClose();
      console.error("Error during the request:", error.message);
    }
  };

  return (
    <>
      {loader ? (
        <CircularProgress
          sx={{
            position: "absolute",
            top: "calc(50% - 20px)",
            left: "calc(50% - 20px)",
          }}
        />
      ) : null}
      <Box sx={{ position: "relative" }}>
        <Box sx={{ position: "relative", opacity: loader ? ".4" : 1 }}>
          <TextField
            sx={{ width: "100%", marginBottom: "1rem", textTransform: "capitalize" }}
            value={formValues?.first_name || ""}
            onChange={handleChange}
            name="first_name"
            label={t("studentFirstName")}
            variant="outlined"
            required
            error={!!formErrors.first_name}
            helperText={formErrors.first_name || ""}
          />
          <TextField
            sx={{ width: "100%", marginBottom: "1rem", textTransform: "capitalize" }}
            value={formValues?.last_name || ""}
            onChange={handleChange}
            name="last_name"
            label={t("studentLastName")}
            variant="outlined"
            required
            error={!!formErrors.last_name}
            helperText={formErrors.last_name || ""}
          />
          <TextField
            sx={{ width: "100%", marginBottom: "1rem", textTransform: "capitalize" }}
            value={formValues?.email_address || ""}
            onChange={handleChange}
            name="email_address"
            label={t("emailAddress")}
            variant="outlined"
            type="email"
            required
            error={!!formErrors.email_address}
            helperText={formErrors.email_address || ""}
          />
          <Box sx={{ marginBottom: "1rem" }}>
            <PhoneCountryInput
              formValues={formValues}
              setFormValues={setFormValues}
              error={!!formErrors.phone}
              helperText={formErrors.phone || ""}
            />
          </Box>
          <FormControl fullWidth sx={{ width: "100%", marginBottom: "1rem" }}>
            <InputLabel sx={{ textTransform: "capitalize" }}>{t("gender")}</InputLabel>
            <Select
              sx={{ textTransform: "capitalize" }}
              value={formValues?.student_gender}
              name="student_gender"
              label="Gender"
              onChange={handleChange}
            >
              <MenuItem sx={{ textTransform: "capitalize" }} value={"male"}>{t("male")}</MenuItem>
              <MenuItem sx={{ textTransform: "capitalize" }} value={"female"}>{t("female")}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            sx={{ width: "100%", marginBottom: "1rem", textTransform: "capitalize" }}
            value={formValues?.form_message}
            onChange={handleChange}
            name="form_message"
            label={t("message")}
            variant="outlined"
            multiline
            rows={3}
            required
          />
          <Button
            sx={{ marginTop: "1rem", height: "56px" }}
            onClick={handleCreateStudent}
            disabled={
              !formValues?.first_name ||
              !formValues?.last_name ||
              !formValues?.email_address ||
              !formValues?.phone ||
              !formValues?.student_gender ||
              !formValues?.form_message ||
              formErrors.email_address ||
              formErrors?.phone
            }
            fullWidth
            variant="contained"
            color="primary"
          >
            {t("create")}
          </Button>
        </Box>
      </Box>
    </>
  );
}
